import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Eye, EyeOff, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { formatPrice } from '@/lib/formatPrice';
import {
  DEFAULT_WEEKDAY_AVAILABILITY,
  WEEKDAY_OPTIONS,
  formatWeekdayAvailabilityLabel,
  getRomeWeekday,
  normalizeAvailableWeekdays,
} from '@/lib/weekdayAvailability';

const ADMIN_API = `${window.location.origin}/menu-digitale/api/admin`;

const normalizeCategories = (raw) =>
  (Array.isArray(raw) ? raw : []).map((category, catIndex) => ({
    ...category,
    sort_order: Number(category.sort_order ?? catIndex + 1),
    is_active: category.is_active !== false,
    items: Array.isArray(category.items)
      ? category.items.map((item, itemIndex) => ({
          ...item,
          sort_order: Number(item.sort_order ?? itemIndex + 1),
          show_price: item.show_price !== false,
          is_active: item.is_active !== false,
          badges: Array.isArray(item.badges) ? item.badges : [],
          available_weekdays: normalizeAvailableWeekdays(item.available_weekdays),
        }))
      : [],
  }));

export default function AdminMenus() {
  const auth = useAuth();
  const navigate = useNavigate();

  const token =
    auth?.token ||
    auth?.user?.token ||
    auth?.currentUser?.token ||
    localStorage.getItem('sapori1989_admin_auth') ||
    null;

  const authHeaders = useCallback(
    (extra = {}) => ({
      ...extra,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(getRomeWeekday);
  const [hasChanges, setHasChanges] = useState(false);

  const selectedDayMeta = useMemo(
    () => WEEKDAY_OPTIONS.find((day) => day.value === selectedDay) || WEEKDAY_OPTIONS[0],
    [selectedDay]
  );

  const fetchFullMenu = useCallback(async () => {
    try {
      const response = await fetch(`${ADMIN_API}/full-menu/index.php`, {
        headers: authHeaders(),
        credentials: 'include',
        cache: 'no-store',
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || data?.error || 'Errore nel caricamento del menu completo');
      }

      setCategories(normalizeCategories(data?.categories));
      setHasChanges(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!token) return;
    fetchFullMenu();
  }, [token, fetchFullMenu]);

  const saveAvailability = async () => {
    setSaving(true);

    try {
      const payload = {
        categories: categories.map((category, catIndex) => ({
          name: category.name?.trim() || '',
          slug: category.slug?.trim() || '',
          description: category.description?.trim() || '',
          sort_order: catIndex + 1,
          is_active: !!category.is_active,
          items: (Array.isArray(category.items) ? category.items : []).map((item, itemIndex) => ({
            name: item.name?.trim() || '',
            description: item.description?.trim() || '',
            price_type: item.price_type || 'portion',
            price_value: item.price_value === '' || item.price_value === null ? null : Number(item.price_value),
            show_price: !!item.show_price,
            image_url: item.image_url || null,
            badges: Array.isArray(item.badges) ? item.badges : [],
            sort_order: itemIndex + 1,
            is_active: !!item.is_active,
            available_weekdays: normalizeAvailableWeekdays(item.available_weekdays),
          })),
        })),
      };

      const response = await fetch(`${ADMIN_API}/full-menu/save.php`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || data?.error || 'Errore nel salvataggio');
      }

      toast.success(`Disponibilita di ${selectedDayMeta.label} salvata!`);
      setHasChanges(false);
      fetchFullMenu();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleItemForDay = (categoryId, itemId, enabled) => {
    setCategories((prev) =>
      prev.map((category) => {
        if (category.id !== categoryId) return category;

        return {
          ...category,
          items: (Array.isArray(category.items) ? category.items : []).map((item) => {
            if (item.id !== itemId) return item;

            const currentDays = normalizeAvailableWeekdays(item.available_weekdays);
            const nextDays = enabled
              ? [...currentDays, selectedDay]
              : currentDays.filter((day) => day !== selectedDay);

            return {
              ...item,
              available_weekdays: enabled
                ? normalizeAvailableWeekdays(nextDays)
                : Array.from(new Set(nextDays)).sort((a, b) => a - b),
            };
          }),
        };
      })
    );
    setHasChanges(true);
  };

  const setCategoryAvailabilityForDay = (categoryId, enabled) => {
    setCategories((prev) =>
      prev.map((category) => {
        if (category.id !== categoryId) return category;

        return {
          ...category,
          items: (Array.isArray(category.items) ? category.items : []).map((item) => {
            const currentDays = normalizeAvailableWeekdays(item.available_weekdays);
            const nextDays = enabled
              ? normalizeAvailableWeekdays([...currentDays, selectedDay])
              : currentDays.filter((day) => day !== selectedDay);

            return {
              ...item,
              available_weekdays: enabled
                ? nextDays
                : Array.from(new Set(nextDays)).sort((a, b) => a - b),
            };
          }),
        };
      })
    );
    setHasChanges(true);
  };

  const categoryStats = useMemo(() => {
    return categories.reduce((acc, category) => {
      const items = Array.isArray(category.items) ? category.items : [];
      const activeToday = items.filter(
        (item) =>
          category.is_active &&
          item.is_active &&
          normalizeAvailableWeekdays(item.available_weekdays).includes(selectedDay)
      ).length;

      acc[category.id] = {
        total: items.length,
        activeToday,
      };

      return acc;
    }, {});
  }, [categories, selectedDay]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Disponibilita Settimanale</h1>
            <p className="text-muted-foreground">
              Il sito usa un solo menu completo. Da qui decidi quali piatti mostrare ogni giorno.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/full-menu')}>
              Apri Menu Completo
            </Button>
            <Button
              onClick={saveAvailability}
              disabled={saving || !hasChanges}
              className="gradient-instagram text-white"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Salvataggio...' : `Salva ${selectedDayMeta.label}`}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-[#8f7f74]" />
              Giorno da gestire
            </CardTitle>
            <CardDescription>
              Scegli il giorno e attiva o disattiva i piatti del menu completo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
              {WEEKDAY_OPTIONS.map((day) => (
                <Button
                  key={day.value}
                  type="button"
                  variant={selectedDay === day.value ? 'default' : 'outline'}
                  className={selectedDay === day.value ? 'gradient-instagram text-white' : ''}
                  onClick={() => setSelectedDay(day.value)}
                >
                  {day.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-dashed bg-muted/20">
          <CardContent className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-foreground">Logica attiva del sito</p>
              <p className="text-sm text-muted-foreground">
                La home e il menu pubblico leggono il menu completo e mostrano solo i piatti attivi per {selectedDayMeta.label.toLowerCase()}.
              </p>
            </div>
            <Badge variant={hasChanges ? 'destructive' : 'secondary'}>
              {hasChanges ? 'Modifiche non salvate' : 'Tutto salvato'}
            </Badge>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {categories.map((category) => {
            const stats = categoryStats[category.id] || { total: 0, activeToday: 0 };
            const categoryDisabled = !category.is_active;
            const items = Array.isArray(category.items) ? category.items : [];

            return (
              <Card key={category.id} className="border-dashed">
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      <CardDescription>
                        {stats.activeToday} di {stats.total} piatti visibili il {selectedDayMeta.label.toLowerCase()}
                      </CardDescription>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {categoryDisabled ? (
                        <Badge variant="outline">Categoria disattivata nel menu completo</Badge>
                      ) : null}

                      <Button
                        variant="outline"
                        disabled={categoryDisabled || items.length === 0}
                        onClick={() => setCategoryAvailabilityForDay(category.id, true)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Attiva tutti
                      </Button>

                      <Button
                        variant="outline"
                        disabled={categoryDisabled || items.length === 0}
                        onClick={() => setCategoryAvailabilityForDay(category.id, false)}
                      >
                        <EyeOff className="mr-2 h-4 w-4" />
                        Disattiva tutti
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {items.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      Nessun piatto presente in questa categoria.
                    </div>
                  ) : (
                    items.map((item) => {
                      const itemDisabled = categoryDisabled || !item.is_active;
                      const isEnabledToday = normalizeAvailableWeekdays(item.available_weekdays).includes(selectedDay);

                      return (
                        <div
                          key={item.id}
                          className={`rounded-xl border p-4 ${itemDisabled ? 'border-dashed border-border bg-muted/20 opacity-70' : 'border-border/70 bg-card'}`}
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-foreground">{item.name}</span>

                                {!item.is_active ? (
                                  <Badge variant="outline">Disattivato nel menu completo</Badge>
                                ) : null}

                                {Array.isArray(item.badges) && item.badges.map((badge) => (
                                  <Badge key={badge} variant="outline" className="text-xs">
                                    {badge}
                                  </Badge>
                                ))}
                              </div>

                              {item.description ? (
                                <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                              ) : null}

                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                {item.price_value !== null && item.price_value !== undefined && item.price_value !== '' ? (
                                  <Badge variant="secondary">
                                    {item.price_type === 'fixed'
                                      ? `${formatPrice(item.price_value)} fisso`
                                      : `${formatPrice(item.price_value)} / 300g`}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">Senza prezzo</Badge>
                                )}

                                <Badge variant="outline">
                                  {formatWeekdayAvailabilityLabel(item.available_weekdays)}
                                </Badge>
                              </div>
                            </div>

                            <div className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-muted/20 px-4 py-3 lg:min-w-[220px]">
                              <div>
                                <p className="text-sm font-medium text-foreground">{selectedDayMeta.label}</p>
                                <p className="text-xs text-muted-foreground">
                                  {itemDisabled
                                    ? 'Attiva prima il piatto nel menu completo'
                                    : isEnabledToday
                                      ? 'Visibile sul sito'
                                      : 'Nascosto sul sito'}
                                </p>
                              </div>

                              <Switch
                                checked={isEnabledToday}
                                disabled={itemDisabled}
                                onCheckedChange={(checked) => toggleItemForDay(category.id, item.id, checked)}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
